import React, { useEffect, useRef, useState } from "react";
import "../Styles/VideoAndDocumentsUploader.css";
import { delete_client_video } from "../api/serverWrapper";
import {
  getUploadValidationError,
  getVideoDurationError,
} from "../utils/uploadLimits";

const VideoAndDocumentsUploader = ({
  videoFile,
  setVideoFile,
  communitieDocs,
  docFile,
  setDocFile,
  docName,
  setDocName,
  selectedReligion,
  userId,
  otherMediaFiles = [],
  autoGenerateFromPhotos = false,
  isGeneratingIntroVideo = false,
}) => {
  const videoInputRef = useRef(null);
  const docInputRef = useRef(null);
  const [deletingVideo, setDeletingVideo] = useState(false);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState("");

  useEffect(() => {
    if (!(videoFile instanceof File)) {
      setVideoPreviewUrl("");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(videoFile);
    setVideoPreviewUrl(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [videoFile]);

  // ===== VIDEO HANDLERS =====
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validationError = getUploadValidationError({
      videoFile: file,
      documentFile: docFile,
      otherFiles: otherMediaFiles,
    });
    const durationError = validationError
      ? null
      : await getVideoDurationError(file);
    if (validationError || durationError) {
      alert(validationError || durationError);
      e.target.value = "";
      return;
    }

    setVideoFile(file);
  };

  const removeVideo = async () => {
    // Existing video from server → confirm + call delete API
    if (videoFile && !(videoFile instanceof File) && videoFile.isFromServer) {
      const confirmDelete = window.confirm(
        "Do you want to delete this introduction video?",
      );
      if (!confirmDelete) return;

      const actualUrl = videoFile.source?.uri
        ? videoFile.source.uri.split("?")[0]
        : videoFile.url?.split("?")[0] || videoFile.preview?.split("?")[0];

      if (!actualUrl) {
        alert("Could not resolve video URL for deletion.");
        return;
      }

      try {
        setDeletingVideo(true);
        const resp = await delete_client_video({
          UserId: userId,
          ProfileVideo: actualUrl,
        });

        if (resp?.status !== 200 && resp?.code !== 200) {
          alert("Failed to delete video.");
          return;
        }
      } catch (error) {
        console.error("Error deleting video:", error);
        alert("Failed to delete video.");
        return;
      } finally {
        setDeletingVideo(false);
      }
    }

    // Either a freshly-picked file (no API call needed) or delete succeeded
    setVideoFile(null);
    if (videoInputRef.current) {
      videoInputRef.current.value = "";
    }
  };

  return (
    <div className="Uploader-container">
      {/* ================= INTRODUCTION VIDEO ================= */}
      <div className="Uploader-section">
        <h3 className="Uploader-heading">Introduction Video</h3>
        <p className="Uploader-subheading">Optional</p>
        {autoGenerateFromPhotos && (
          <p className="Uploader-auto-video-note">
            If you add photos and skip uploading a video, we will create a short
            slideshow with gentle music.
          </p>
        )}
        {isGeneratingIntroVideo && (
          <p className="Uploader-generating-video" role="status">
            Creating your introduction video from your photos…
          </p>
        )}

        <div
          className="Uploader-video-box"
          onClick={() => {
            if (
              !videoFile &&
              !isGeneratingIntroVideo &&
              videoInputRef.current
            ) {
              videoInputRef.current.click();
            }
          }}
        >
          {videoFile ? (
            <div className="Uploader-video-wrapper">
              <button
                className="Uploader-video-remove"
                onClick={(e) => {
                  e.stopPropagation(); // 🔥 prevent opening picker
                  removeVideo();
                }}
              >
                {deletingVideo ? "…" : "✕"}
              </button>

              <video
                width="550px"
                height="250px"
                controls
                src={
                  videoFile instanceof File
                    ? videoPreviewUrl
                    : videoFile.url
                }
              >
                Your browser does not support the video tag.
              </video>
            </div>
          ) : (
            <>
              <div className="Uploader-icon">&#8679;</div>
              <p className="Uploader-upload-text">Upload Video</p>
              <small>Up to 20 MB and 60 seconds</small>
            </>
          )}

          <input
            type="file"
            accept="video/*"
            ref={videoInputRef}
            hidden
            disabled={isGeneratingIntroVideo}
            onChange={handleVideoUpload}
          />
        </div>
      </div>

      {/* ================= DOCUMENTS ================= */}
      <div className="Uploader-section">
        <h3 className="Uploader-heading">Others</h3>
        <p className="Uploader-subtext">
          Upload other relevant documents (e.g., Kundali, Baptism Certificate,
          etc.) as per your community.
        </p>

        <div className="Uploader-doc-box">
          {/* ===== Document Type Dropdown ===== */}
          <select
            className="Uploader-dropdown"
            value={docName}
            onChange={(e) => setDocName(e.target.value)}
          >
            <option value="">Select Document Type</option>

            {communitieDocs
              ?.filter((item) => item.ReligionId === (selectedReligion || 1))
              .map((item) => (
                <option key={item.id} value={item.DocName}>
                  {item.DocName}
                </option>
              ))}
          </select>

          <div className="Uploader-upload-area">
            {/* ==== Top Right Icons ==== */}
            {docFile && (
              <div className="Uploader-doc-actions">
                {/* 👁 VIEW */}
                {docFile.url && (
                  <button
                    className="Uploader-doc-view"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(docFile.url, "_blank");
                    }}
                  >
                    👁
                  </button>
                )}

                {/* ❌ REMOVE */}
                {docFile && (
                  <button
                    className="Uploader-doc-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDocFile(null);
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            )}

            {/* ==== Upload Area ==== */}
            <div
              onClick={() => {
                if (!docName) {
                  alert("Select Document type");
                  return;
                }
                docInputRef.current.click();
              }}
            >
              {docFile ? (
                <span>{docFile.name}</span>
              ) : (
                <>
                  <span className="Uploader-upload-icon">&#128228;</span>
                  <span>Upload Document</span>
                  <small>PDF, JPEG, PNG. Up to 5 MB</small>
                </>
              )}
            </div>

            <input
              type="file"
              hidden
              ref={docInputRef}
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => {
                if (!docName) {
                  alert("Select Document type");
                  return;
                }

                const file = e.target.files[0];
                if (!file) return;

                const validationError = getUploadValidationError({
                  videoFile,
                  documentFile: file,
                  otherFiles: otherMediaFiles,
                });
                if (validationError) {
                  alert(validationError);
                  e.target.value = "";
                  return;
                }

                setDocFile({
                  name: docName,
                  file,
                  url: URL.createObjectURL(file),
                });
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoAndDocumentsUploader;
